<template>
  <div class="filter-group">
    <label class="filter-label">
      <v-icon small color="#023449" class="mr-1">mdi-calendar-range</v-icon>
      Período
      <v-chip v-if="hasDateRange" x-small color="#e8f5e9" text-color="#10b981" class="ml-auto">Ativo</v-chip>
    </label>

    <div class="date-range-wrapper">
      <v-menu
          v-model="menuDateStart"
          :close-on-content-click="false"
          offset-y
          :disabled="loading"
          :nudge-right="40"
          transition="scale-transition"
          min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
              :value="formatDate(internalValue.start)"
              label="Data inicial"
              prepend-inner-icon="mdi-calendar"
              readonly dense outlined hide-details
              v-bind="attrs" v-on="on"
              :disabled="loading"
          ></v-text-field>
        </template>
        <v-date-picker
            v-model="internalValue.start"
            @input="menuDateStart = false; emitChange()"
            :max="internalValue.end"
            color="#023449"
            locale="pt-BR"
            scrollable
        ></v-date-picker>
      </v-menu>

      <div class="date-separator">até</div>

      <v-menu
          v-model="menuDateEnd"
          :close-on-content-click="false"
          offset-y
          :disabled="loading"
          :nudge-right="40"
          transition="scale-transition"
          min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
              :value="formatDate(internalValue.end)"
              label="Data final"
              prepend-inner-icon="mdi-calendar"
              readonly dense outlined hide-details
              v-bind="attrs" v-on="on"
              :disabled="loading"
          ></v-text-field>
        </template>
        <v-date-picker
            v-model="internalValue.end"
            @input="menuDateEnd = false; emitChange()"
            :min="internalValue.start"
            color="#023449"
            locale="pt-BR"
            scrollable
        ></v-date-picker>
      </v-menu>
    </div>

    <div class="quick-actions">
      <v-btn x-small outlined color="#023449" @click="setQuickDate('today')" :disabled="loading">Hoje</v-btn>
      <v-btn x-small outlined color="#023449" @click="setQuickDate('yesterday')" :disabled="loading">Ontem</v-btn>
      <v-btn x-small outlined color="#023449" @click="setQuickDate('week')" :disabled="loading">7 dias</v-btn>
      <v-btn x-small outlined color="#023449" @click="setQuickDate('month')" :disabled="loading">30 dias</v-btn>

      <quick-dates-menu
          :loading="loading"
          @select-period="setQuickDate"
      />
    </div>
  </div>
</template>

<script>
import QuickDatesMenu from './QuickDatesMenu.vue';

export default {
  name: 'DateRangeFilter',

  components: { QuickDatesMenu },

  props: {
    value: { type: Object, required: true },
    loading: Boolean
  },

  data() {
    return {
      menuDateStart: false,
      menuDateEnd: false,
      internalValue: { ...this.value }
    };
  },

  computed: {
    hasDateRange() {
      return this.internalValue.start || this.internalValue.end;
    }
  },

  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.internalValue = { ...newVal };
      }
    },
    loading(newVal) {
      if (newVal) {
        this.menuDateStart = false;
        this.menuDateEnd = false;
      }
    }
  },

  methods: {
    emitChange() {
      this.$emit('input', { ...this.internalValue });
      this.$emit('change');
    },

    setQuickDate(period) {
      if (this.loading) return;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let start = new Date(today);
      let end = new Date(today);

      switch (period) {
        case 'yesterday':
          start.setDate(start.getDate() - 1);
          end.setDate(end.getDate() - 1);
          break;
        case 'week':
          start.setDate(start.getDate() - 7);
          break;
        case 'month':
          start.setDate(start.getDate() - 30);
          break;
        case 'currentWeek':
          start = this.getStartOfWeek(today);
          end = this.getEndOfWeek(today);
          break;
        case 'lastWeek': {
          const lastWeekDate = new Date(today);
          lastWeekDate.setDate(lastWeekDate.getDate() - 7);
          start = this.getStartOfWeek(lastWeekDate);
          end = this.getEndOfWeek(lastWeekDate);
          break;
        }
        case 'currentMonth':
          start = this.getStartOfMonth(today);
          end = this.getEndOfMonth(today);
          break;
        case 'lastMonth': {
          const lastMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          start = this.getStartOfMonth(lastMonthDate);
          end = this.getEndOfMonth(lastMonthDate);
          break;
        }
        case 'currentQuarter':
          start = this.getStartOfQuarter(today);
          end = this.getEndOfQuarter(today);
          break;
        case 'lastQuarter': {
          const lastQuarterDate = new Date(today);
          lastQuarterDate.setMonth(lastQuarterDate.getMonth() - 3);
          start = this.getStartOfQuarter(lastQuarterDate);
          end = this.getEndOfQuarter(lastQuarterDate);
          break;
        }
        case 'currentYear':
          start = new Date(today.getFullYear(), 0, 1);
          end = new Date(today.getFullYear(), 11, 31);
          break;
        case 'lastYear':
          start = new Date(today.getFullYear() - 1, 0, 1);
          end = new Date(today.getFullYear() - 1, 11, 31);
          break;
      }

      this.internalValue.start = start.toISOString().split('T')[0];
      this.internalValue.end = end.toISOString().split('T')[0];
      this.emitChange();
    },

    getStartOfWeek(date) {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day;
      return new Date(d.setDate(diff));
    },

    getEndOfWeek(date) {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() + (6 - day);
      return new Date(d.setDate(diff));
    },

    getStartOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    },

    getEndOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    },

    getQuarter(date) {
      return Math.floor(date.getMonth() / 3);
    },

    getStartOfQuarter(date) {
      const quarter = this.getQuarter(date);
      return new Date(date.getFullYear(), quarter * 3, 1);
    },

    getEndOfQuarter(date) {
      const quarter = this.getQuarter(date);
      return new Date(date.getFullYear(), quarter * 3 + 3, 0);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString + 'T00:00:00');
      if (isNaN(date)) return '';
      return date.toLocaleDateString('pt-BR');
    }
  }
};
</script>

<style scoped>
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #ffffff;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(2, 52, 73, 0.05);
  transition: all 0.2s ease;
}

.filter-group:hover {
  border-color: #023449;
  box-shadow: 0 2px 4px rgba(2, 52, 73, 0.08);
}

.filter-label {
  display: flex;
  align-items: center;
  font-weight: 700;
  color: #023449;
  font-size: 0.938rem;
  letter-spacing: -0.01em;
  margin-bottom: 4px;
}

.filter-label .v-chip {
  margin-left: auto;
  font-weight: 600;
}

.date-range-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.date-range-wrapper >>> .v-input__control {
  min-height: 44px !important;
}

.date-range-wrapper >>> fieldset {
  border-color: #e5e7eb;
  border-width: 2px;
  transition: all 0.2s ease;
}

.date-range-wrapper >>> .v-input__slot {
  background: #ffffff;
  transition: all 0.2s ease;
}

.date-range-wrapper >>> .v-input__slot:hover fieldset {
  border-color: #023449;
}

.date-range-wrapper >>> .v-text-field__slot input {
  color: #023449;
  font-weight: 500;
}

.date-range-wrapper >>> .v-input--is-disabled fieldset {
  border-color: #e5e7eb !important;
}

.date-range-wrapper >>> .v-input--is-disabled .v-input__slot {
  cursor: not-allowed !important;
  background-color: #f9fafb !important;
}

.date-separator {
  text-align: center;
  font-size: 0.813rem;
  color: #6b7280;
  font-weight: 600;
  padding: 4px 0;
}

.quick-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-actions >>> .v-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  font-size: 0.813rem;
  flex: 1;
  min-width: 65px;
  border-width: 1px !important;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.quick-actions >>> .v-btn:not(.v-btn--disabled):hover {
  background: #023449 !important;
  color: #ffffff !important;
  border-color: #023449 !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(2, 52, 73, 0.15);
}

/* Estilos globais para corrigir o v-menu e v-date-picker */
.filter-group >>> .v-menu__content {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-radius: 12px !important;
  overflow: visible !important;
  max-width: none !important;
}

.filter-group >>> .v-picker {
  border-radius: 12px !important;
  box-shadow: none !important;
  overflow: visible !important;
}

.filter-group >>> .v-date-picker-table {
  height: auto !important;
}

.filter-group >>> .v-picker__body {
  overflow: visible !important;
}

/* Garantir que o calendário não seja cortado */
.filter-group >>> .v-date-picker-header,
.filter-group >>> .v-date-picker-table,
.filter-group >>> .v-picker__body > div {
  overflow: visible !important;
}

/* Remover qualquer overflow hidden que possa estar cortando o conteúdo */
.filter-group >>> .v-picker__body,
.filter-group >>> .v-date-picker-table__events,
.filter-group >>> .v-date-picker-years,
.filter-group >>> .v-date-picker-months {
  overflow: visible !important;
}
</style>